import { intl } from '@fullstory/intl';
import { useSelectableList } from '@fullstory/react-hooks';
import React, { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { identifyComponent } from '../../util';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { List } from '../List';
import { ListItemIcon } from '../ListItemIcon';
import { ListItemText } from '../ListItemText';
import { MenuItem } from '../MenuItem';
import { Popover } from '../Popover';
import { Tooltip } from '../Tooltip';

export interface Action {
  id: string;
  label: string;
  subtext?: string;
  iconName?: React.ComponentPropsWithoutRef<typeof Icon>['name'];
  disabled?: boolean;
  testId?: string;
  tooltip?: React.ReactNode;
}

export type ActionMenuProps = {
  /** An array of options available from the menu */
  actions: ReadonlyArray<Readonly<Action>>;
  /** Called upon selecting an action item */
  onSelect: (id: string) => void;
  /** Tooltip text when the mouse moves over the element. */
  title?: string;
  iconName?: 'ellipsis-vertical' | 'ellipsis-horizontal';
  iconColor?: ComponentProps<typeof Icon>['color'];
  onOpenChanged?: (open: boolean) => void;
} & Pick<React.ComponentProps<typeof IconButton>, 'testId' | 'disabled'> &
  Pick<ComponentProps<typeof Popover>, 'placement'>;

export const ActionMenu: React.FC<ActionMenuProps> = ({
  actions,
  onSelect,
  placement = 'bottom-end',
  testId = 'action-menu-icon-button',
  iconName = 'ellipsis-vertical',
  iconColor,
  title = intl.formatMessage({ defaultMessage: 'More actions', id: 'S8/4ZI' }),
  disabled = false,
  onOpenChanged,
}) => {
  const buttonRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  // the below needs to be a stable reference for the Popover
  const setClosed = useCallback(() => setOpen(false), []);
  const onChange = useCallback(
    (action: Action) => {
      setOpen(false);
      onSelect(action.id);
    },
    [onSelect],
  );
  const { getListItemProps, selectedItemIndex } = useSelectableList(
    actions,
    (maybeAction, action) => maybeAction.id === action.id,
    onChange,
    { active: open },
  );

  const openChangedChecked = useRef(false);

  useEffect(() => {
    if (onOpenChanged && openChangedChecked.current) {
      onOpenChanged(open);
    }

    openChangedChecked.current = true;
  }, [open, onOpenChanged]);

  return (
    <>
      <IconButton
        testId={testId}
        className="action-icon"
        ref={buttonRef}
        iconName={iconName}
        title={title}
        onClick={e => {
          setOpen(o => !o);
          e.stopPropagation();
        }}
        {...identifyComponent('action-menu')}
        iconColor={iconColor}
        disabled={disabled}
      />
      <Popover
        open={open}
        anchor={buttonRef.current}
        onCloseSignal={setClosed}
        testId="action-menu-popover"
        placement={placement}
        arrow
        variant="bordered"
      >
        <List>
          {actions.map((action, i) => {
            const { id, label, subtext, iconName, disabled, testId, tooltip } = action;

            const menuItem = (
              <MenuItem
                {...getListItemProps(action)}
                disabled={disabled}
                highlighted={i === selectedItemIndex}
                testId={testId}
                tabIndex={-1}
              >
                {iconName && <ListItemIcon name={iconName} color="default" />}
                <ListItemText text={label} subtext={subtext} />
              </MenuItem>
            );

            const contents = tooltip ? (
              <Tooltip
                interactive={false}
                placement="left"
                tooltipContent={tooltip}
                asChild
                testId={testId}
              >
                {menuItem}
              </Tooltip>
            ) : (
              menuItem
            );

            return <React.Fragment key={id}>{contents}</React.Fragment>;
          })}
        </List>
      </Popover>
    </>
  );
};
